import {
  Toolbar,
  IconButton,
  Badge,
  InputBase,
  Tooltip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Backdrop,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import React, {
  useCallback,
  useEffect,
  useContext,
  useState,
  useMemo,
} from "react";
import MenuIcon from "@mui/icons-material/Menu";
import { connect, useSelector, useDispatch } from "react-redux";
import debounce from "lodash/debounce";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SortIcon from "@mui/icons-material/Sort";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Neo4jContext, Neo4jContextState } from "use-neo4j/dist/neo4j.context";
import {
  addPageWithDetailsThunk,
  changeActiveGenieListThunk,
  deleteGenieListThunk,
  getAllGenieListsToUserNameWithConnectionProps,
  loadDatabaseListFromNeo4jThunk,
  removePageThunk,
  searchGeneByTextThunk,
} from "../DashboardThunks";
import { updateGlobalParametersThunk } from "../../settings/SettingsThunks";
import FormControl from "@mui/material/FormControl";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import { updateDatabaseThunk } from "../../application/ApplicationThunks";
import GeneListComponent from "../../component/genelistcomponent";
import Switch from "@mui/material/Switch";
import Avatar from "@mui/material/Avatar";
import { updateStandAlone } from "../../application/ApplicationActions";
import { addReportThunk, createNotificationThunk } from "../../page/PageThunks";
import { getExperimentData } from "../../api/heatmap";
import { addPageWithDetails } from "../DashboardActions";

export const NeoDashboardHeaderTitleBar = ({
  dashboardTitle,
  open,
  setDashboardTitle,
  editable,
  standalone,
  handleDrawerOpen,
  relationshipHelpModalOpen,
  showLoadTooltip,
  showSearchTooltip,
  setShowLoadTooltip,
  setShowSearchTooltip,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [databases, setDatabases] = useState([]);
  const [searchedGenies, setSearchedGenies] = useState([]);
  const openMenuBar = Boolean(anchorEl);
  const { driver } = useContext<Neo4jContextState>(Neo4jContext);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const dashboard = useSelector((state: any) => state?.dashboard);
  const applicationState = useSelector((state: any) => state?.application);
  const connectionState = useSelector(
    (state: any) => state?.application?.connection
  );
  const [activeDb, setActiveDb] = useState("");
  const [viewerMode, setViewMode] = useState(!applicationState?.admin);
  const [experiments, setExperiments] = useState([]);
  const [selectedExperiment, setSelectedExperiment] = useState();

  const activeGenieList = dashboard?.addedGenieLists.find(
    (item) => item?.active_genelist
  );

  // Get experiments from Neo4j Experiment nodes
  async function getExperiments() {
    try {
      if (!driver) return;

      const session = driver.session({
        database: connectionState?.database || 'neo4j'
      });

      const result = await session.run(
        `MATCH (exp:Experiment)
         RETURN exp.experiment_name AS experiment_value,
                exp.experiment_userfriendly_name AS experiment_name,
                exp.samples_name AS samples_name,
                exp.experiment_name AS experiment_id
         ORDER BY exp.experiment_name`
      );

      await session.close();

      const experimentsData = result.records.map((record) => ({
        experiment_id: record.get('experiment_id'),
        experiment_name: record.get('experiment_name'),
        experiment_value: record.get('experiment_value'),
        samples_name: record.get('samples_name'),
        experiment_table: 'expression',
        visibility: true,
        default_selection: false,
        tool_category: 'expression',
        tools: 'exheatmap,explot',
        method: 'TPM'
      }));

      if (experimentsData.length > 0) {
        setExperiments(experimentsData);
        setSelectedExperiment(experimentsData[0]);
        dispatch(
          updateGlobalParametersThunk({
            neodash_experiment_value: experimentsData[0]?.experiment_value,
          })
        );
      }
    } catch (error) {
      console.error('Error fetching experiments from Neo4j:', error);
      // Fallback to API if Neo4j query fails
      try {
        const response = await getExperimentData(connectionState?.database);
        if (response?.status === 200) {
          setExperiments(response?.data);
          setSelectedExperiment(response?.data[0]);
          dispatch(
            updateGlobalParametersThunk({
              neodash_experiment_value: response?.data[0]?.experiment_value,
            })
          );
        }
      } catch (apiError) {
        console.error('Error fetching experiments from API:', apiError);
      }
    }
  }

  useEffect(() => {
    getExperiments();
    setActiveDb(connectionState?.database);
    dispatch(
      loadDatabaseListFromNeo4jThunk(driver, (data) => {
        setDatabases(data);
        const availableIndex = data.findIndex(
          (item) => item === connectionState?.database
        );
        if (availableIndex === -1) {
          // No db found, show an error
          // dispatch(createNotificationThunk("Select a Database", "Click the three lines in the top-left corner"));
        }
      })
    );
  }, []);

  const deleteGenieList = (item) => {
    dispatch(
      deleteGenieListThunk(
        driver,
        applicationState?.connection?.database,
        item.id
      )
    );
  };

  const handleDBChange = (event) => {
    setActiveDb(event?.target?.value);
    if (event?.target?.value) {
      dispatch(updateDatabaseThunk(event.target.value));
      dispatch(
        getAllGenieListsToUserNameWithConnectionProps(
          driver,
          event.target.value
        )
      );
    }
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const changeActiveGenieList = (item) => {
    setAnchorEl(null);

    if (item?.id) {
      const activeItemId = dashboard?.addedGenieLists.findIndex(
        (item) => item?.active_genelist
      );

      if (activeItemId !== -1) {
        dispatch(
          changeActiveGenieListThunk(
            driver,
            applicationState?.connection?.database,
            dashboard?.addedGenieLists[activeItemId]?.id,
            false,
            (data) => {
              dispatch(
                updateGlobalParametersThunk({
                  neodash_genelist_name: item?.genelist?.split(","),
                })
              );
              dispatch(
                changeActiveGenieListThunk(
                  driver,
                  applicationState?.connection?.database,
                  item?.id,
                  !item?.active_genelist
                )
              );
            }
          )
        );
      } else {
        dispatch(
          changeActiveGenieListThunk(
            driver,
            applicationState?.connection?.database,
            item?.id,
            !item?.active_genelist,
            (data) => {
              dispatch(
                updateGlobalParametersThunk({
                  neodash_genelist_name: item?.genelist?.split(","),
                })
              );
            }
          )
        );
      }
    }
  };

  const [dashboardTitleText, setDashboardTitleText] =
    React.useState(dashboardTitle);
  const debouncedDashboardTitleUpdate = useCallback(
    debounce(setDashboardTitle, 250),
    []
  );

  const handleSearchSelectGenie = (values) => {
    const geneList = values.map((item) => item.name);
    if (geneList.length > 0) {
      const newGeneList = activeGenieList?.genelist
        ?.split(",")
        .concat(geneList);

      dispatch(
        updateGlobalParametersThunk({
          neodash_genelist_name: newGeneList,
        })
      );
    } else {
      const newGeneList = activeGenieList?.genelist?.split(",");
      dispatch(
        updateGlobalParametersThunk({
          neodash_genelist_name: newGeneList,
        })
      );
    }
  };

  function handleSelectExperiment(event) {
    const experimentId = event.target.value;
    const selectedExperiment = experiments.find(
      (item) => item.experiment_id === experimentId
    );
    setSelectedExperiment(selectedExperiment);
    dispatch(
      updateGlobalParametersThunk({
        neodash_experiment_value: selectedExperiment?.experiment_value,
      })
    );
  }

  useEffect(() => {
    // Reset text to the dashboard state when the page gets reorganized.
    if (dashboardTitle !== dashboardTitleText) {
      setDashboardTitleText(dashboardTitle);
    }
  }, [dashboardTitle]);

  const searchGenieByText = (text) => {
    if (text.length > 0) {
      dispatch(
        searchGeneByTextThunk(
          driver,
          applicationState?.connection?.database,
          text,
          (values) => {
            setSearchedGenies(values);
          }
        )
      );
    } else {
      setSearchedGenies([]);
    }
  };

  // State for the profile menu
  // State for the profile dialog
  const [profilePopupOpen, setProfilePopupOpen] = React.useState(false);
  const handleProfilePopupOpen = (event) => {
    setProfilePopupOpen(true);
  };
  const handleProfilePopupClose = () => {
    setProfilePopupOpen(false);
  };

  const addGenieDetailsPageAndReport = (gene) => {
    // Need to check whether existing page is available or not
    const existingPage = dashboard?.pages?.find(
      (item) => item?.title === "Gene Details"
    );
    if (existingPage) {
      dispatch(removePageThunk(dashboard?.pages?.indexOf(existingPage)));
    }
    dispatch(addPageWithDetailsThunk({ title: "Gene Details", reports: [] }));
    dispatch(
      addReportThunk(0, 0, 8, 5, {
        title: gene?.name,
        query: "MATCH(n:Gene) WHERE n.name = '" + gene?.name + "' RETURN n",
        type: "geneDetails",
      })
    );
    dispatch(
      updateGlobalParametersThunk({
        plantgenie_selected_gene: gene?.name,
      })
    );
  };

  const updateGeneIdUrl = (gene) => {
    addGenieDetailsPageAndReport(gene);
  };

  const content = (
    <>

      <Toolbar
        key={1}
        style={{
          paddingRight: 24,
          minHeight: "72px",
          zIndex: 1201,
          background: '#ffffff',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {!standalone ? (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            style={
              open ? { display: "none" } : { marginRight: 8, marginLeft: -15, color: "#000" }
            }
          >
            <MenuIcon />
          </IconButton>
        ) : (
          <></>
        )}
        <img
          src="plogo.png"
          style={{ color: "#ffffffdd", width: "36px", zIndex: 5, marginLeft: 8 }} // Increased size for logo
          alt="Logo"
        ></img>
        <InputBase
          id="left-aligned"
          style={{
            textAlign: "left",
            fontSize: "20px",
            flexGrow: 1,
            color: "black",
            marginLeft: 2,
          }}
          placeholder="Dashboard Name..."
          fullWidth
          maxRows={4}
          value={dashboardTitleText}
          onChange={(event) => {
            if (editable) {
              setDashboardTitleText(event.target.value);
              debouncedDashboardTitleUpdate(event.target.value);
            }
          }}
        />
        <Menu
          id="gene-list-menu"
          anchorEl={anchorEl}
          open={openMenuBar}
          onClose={closeMenu}
          MenuListProps={{
            "aria-labelledby": "genie-select-button",
          }}
        >
          {dashboard?.addedGenieLists?.map((item) => (
            <GeneListComponent
              item={item}
              key={item.id}
              closeMenu={closeMenu}
              changeActiveGenieList={changeActiveGenieList}
              deleteGenieList={deleteGenieList}
              driver={driver}
              database={applicationState?.connection?.database}
            />
          ))}
        </Menu>
        {experiments.length > 0 && selectedExperiment && (
          <Box sx={{ maxWidth: 400, mr: 1 }}>
            <FormControl fullWidth>
              <InputLabel
                id="demo-simple-select-label"
                sx={{
                  color: "#000",
                  "&.Mui-focused": {
                    color: "#000",
                  },
                }}
              >
                Experiment
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={selectedExperiment?.experiment_id}
                label="Select Experiment"
                onChange={handleSelectExperiment}
                size="small"
                style={{ width: "200px", color: "black" }}

              >
                {experiments.map((item) => (
                  <MenuItem key={item?.experiment_id} value={item?.experiment_id}>
                    {item?.experiment_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Tooltip
          open={showSearchTooltip}
          title="Search your desired genes here"
          arrow
          placement="bottom"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: '#859f64',
                color: 'white',
                fontSize: '16px',
                padding: '12px 16px',
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1300,
                '& .MuiTooltip-arrow': {
                  color: '#859f64',
                },
              },
            },
            popper: {
              sx: {
                zIndex: 1300,
              },
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
            }}
          >
            <Autocomplete
              disablePortal
              id="autocomplete-search"
              options={searchedGenies}
              onChange={(event, value, reason) => {
                if (value) {
                  setShowSearchTooltip && setShowSearchTooltip(false);
                  updateGeneIdUrl(value)
                }
              }}
              onFocus={() => setShowSearchTooltip && setShowSearchTooltip(false)}
              sx={{
                width: 400,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                borderRadius: 1,
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                    borderWidth: 1,
                  },
                  "&:hover fieldset": {
                    borderColor: "white",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.light,
                    borderWidth: 2,
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#666",
                      fontWeight: 400,
                      fontSize: "14px",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#9a9a9aff",
                      fontWeight: 400,
                      fontSize: "14px",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: theme.palette.primary.light,
                    },
                  }}
                  label="Search gene IDs, descriptions or synonyms"
                  onChange={(event) => {
                    searchGenieByText(event.target.value)
                  }}
                />
              )}
              size="small"
            />
          </Box>
        </Tooltip>


        {/* {applicationState?.admin && (
        <Tooltip
          title={viewerMode ? "Switch to admin mode" : "Switch to viewer mode"}
          placement="left"
          aria-label="host"
        >
          <Switch
            checked={viewerMode}
            onChange={(event, value) => {
              setViewMode(!viewerMode);
              dispatch(updateStandAlone(value));
            }}
          />
        </Tooltip>
      )} */}
        <Avatar sx={{ mr: 1, bgcolor: theme.palette.primary.main }} alt="0">
          {activeGenieList?.gene_count ? activeGenieList?.gene_count : 0}
        </Avatar>
        {dashboard?.addedGenieLists?.length > 0 && (
          <Tooltip title="Saved Genie Lists" placement="left" aria-label="host">
            <IconButton
              id="genie-select-button"
              aria-controls={openMenuBar ? "gene-list-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={openMenuBar ? "true" : undefined}
              style={{
                background: "#E4E1D6",
                borderRadius: "4px",
                padding: "1px",
                marginRight: "10px",
              }}
              onClick={handleClick}
            >
              <SortIcon
                style={{
                  padding: 6,
                  color: "#ffffffdd",
                  width: "36px",
                  height: "36px",
                  fontSize: "1.3rem",
                  zIndex: 5,
                }}
                fontSize="small"
              />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="" placement="bottom" aria-label="host">
          <IconButton
            style={{
              background: "#C7D7AA",
              borderRadius: "50%",
              padding: "8px",
              marginRight: "8px",
              width: "40px",
              height: "40px",
            }}
            sx={{
              "&:hover": {
                background: "#B5C799",
              },
            }}
            onClick={() => {
              relationshipHelpModalOpen();
            }}
          >
            <InfoOutlinedIcon
              style={{
                color: "#000",
                fontSize: "1.25rem",
              }}
              fontSize="small"
            />
          </IconButton>
        </Tooltip>

        {/* Profile Avatar Icon */}
        <Tooltip title="Profile" placement="left" aria-label="profile">
          <IconButton onClick={handleProfilePopupOpen} sx={{ p: 0.5 }}>
            <Avatar src="/broken-image.jpg" />
          </IconButton>
        </Tooltip>
        {/* <Menu
          anchorEl={profileAnchorEl}
          open={Boolean(profileAnchorEl)}
          onClose={handleProfileMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              bgcolor: theme.palette.primary.main, // Match toolbar background
              color: "white",
              boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
            },
          }}
        >
          <MenuItem onClick={handleProfileMenuClose}>Profile</MenuItem>
          <MenuItem onClick={handleProfileMenuClose}>Settings</MenuItem>
          <MenuItem onClick={handleProfileMenuClose}>Logout</MenuItem>
        </Menu> */}
        {profilePopupOpen && (
          <div
            style={{
              position: "absolute",
              top: "100%", // Position just below the toolbar
              right: 0,
              backgroundColor: "white",
              color: "black",
              width: "100%",
              borderRadius: "8px",
              padding: 120,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
              // padding: "16px",
              zIndex: 1300, // Above other elements
              display: "flex",
            }}
          >
            <div style={{ width: '320px', flexShrink: 0, padding: 50 }}>
              <Avatar
                src="/broken-image.jpg"
                alt="ABC Silva"
                style={{ width: '100px', height: '100px', margin: '0 auto 8px' }}
              />
              <Typography variant="body2" align="center" style={{ color: '#757575' }}>
                ABC Silva<br />abcsliva@gmail.com
              </Typography>
              <Button
                variant="outlined"
                size="small"
                style={{ marginTop: '8px', width: '100%', color: '#757575', borderColor: '#757575' }}
                onClick={handleProfilePopupClose}
              >
                Change to admin View
              </Button>
              <Button
                variant="contained"
                size="small"
                style={{ marginTop: '8px', width: '100%', backgroundColor: '#C7D7AA', color: '#000' }}
                onClick={handleProfilePopupClose}
              >
                Login to another account
              </Button>
              <Button
                variant="contained"
                size="small"
                style={{ marginTop: '8px', width: '100%', backgroundColor: '#C7D7AA', color: '#000' }}
                onClick={handleProfilePopupClose}
              >
                Log Out
              </Button>
            </div>
            <div style={{ flexGrow: 1, padding: 60 }}>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
              <TextField
                label="Full Name"
                value="John Smith de Silva"
                fullWidth
                margin="normal"
                disabled
                InputProps={{ style: { color: 'black' } }}
                InputLabelProps={{ style: { color: 'black' } }}
                sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#ccc' } } }}
              />
              <div style={{ display: 'flex', gap: '16px' }}>
                <TextField
                  label="Email Address"
                  value="johnsmith@gmail.com"
                  fullWidth
                  margin="normal"
                  disabled
                  InputProps={{ style: { color: 'black' } }}
                  InputLabelProps={{ style: { color: 'black' } }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#ccc' } }, flex: 2 }}
                />
                <TextField
                  label="Phone Number"
                  value="+94 71234 5678"
                  fullWidth
                  margin="normal"
                  disabled
                  InputProps={{ style: { color: 'black' } }}
                  InputLabelProps={{ style: { color: 'black' } }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#ccc' } }, flex: 1 }}
                />
              </div>
              <TextField
                label="Address"
                value="Anywhere, 1st Street, Colombo, Sri Lanka"
                fullWidth
                margin="normal"
                disabled
                InputProps={{ style: { color: 'black' } }}
                InputLabelProps={{ style: { color: 'black' } }}
                sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#ccc' } } }}
              />
              <Typography variant="h6" gutterBottom style={{ marginTop: '24px' }}>
                Change Password
              </Typography>
              <TextField
                label="Current Password"
                type="password"
                value="********"
                fullWidth
                margin="normal"
                InputProps={{ style: { color: 'black' } }}
                InputLabelProps={{ style: { color: 'black' } }}
                sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#ccc' } } }}
              />
              <div style={{ display: 'flex', gap: '16px' }}>
                <TextField
                  label="New Password"
                  type="password"
                  value=""
                  fullWidth
                  margin="normal"
                  InputProps={{ style: { color: 'black' } }}
                  InputLabelProps={{ style: { color: 'black' } }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#ccc' } }, flex: 1 }}
                />
                <TextField
                  label="Confirm Password"
                  type="password"
                  value=""
                  fullWidth
                  margin="normal"
                  InputProps={{ style: { color: 'black' } }}
                  InputLabelProps={{ style: { color: 'black' } }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#ccc' } }, flex: 1 }}
                />
              </div>
              <Button
                variant="contained"
                style={{ marginTop: '24px', backgroundColor: '#C7D7AA', color: '#000', }}
                onClick={handleProfilePopupClose}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </Toolbar>
    </>
  );
  return content;
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(NeoDashboardHeaderTitleBar);
